import { useRef, useState } from 'react'
import { DomPosition } from 'klinecharts'
import type { Chart, KLineData } from 'klinecharts'
import { Baseline, Bold, Copy, PaintBucket, Trash2, Type } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ColorPopover, rgba } from './WorkbenchColorPicker'
import { BitsPopover, Grip, ToolbarButton, type DragOffset } from './WorkbenchFloatingBits'
import { asPixel, asPoint } from './types'
import type { NotePoint, WorkbenchNote } from './types'

// On-chart notes: text + callout, rendered as HTML anchored to chart points so they can be
// typed INLINE (TV-style), always visible. A callout is a speech bubble with a tail to a
// candle's O/H/L/C. Text is free-floating. Positions recompute on `nonce` (scroll / zoom / drag).
export const NOTE_SIZES = [12, 14, 16, 20, 24]
export const CALLOUT_BG = '#f8c471'
export const CALLOUT_TEXT = '#1a1a1a'

// A darker shade of a fill (hex or rgba) — used for the callout's border + beak outline so the
// outline tracks whatever bubble colour the user picks.
function darken(color: string, f = 0.72, alpha?: number): string {
  let r = 0
  let g = 0
  let b = 0
  let a = 1
  if (color.startsWith('#')) {
    const n = parseInt(color.slice(1), 16)
    r = (n >> 16) & 255
    g = (n >> 8) & 255
    b = n & 255
  } else {
    const m = color.match(/[\d.]+/g) ?? []
    r = Number(m[0]) || 0
    g = Number(m[1]) || 0
    b = Number(m[2]) || 0
    a = m[3] != null ? Number(m[3]) : 1
  }
  return `rgba(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)}, ${alpha != null ? alpha : a})`
}

/** Clock-based note id; module scope keeps the impure call out of the render path. */
const newNoteId = (): string => `note-${Date.now()}`

/**
 * Fallback plot width from the wrapper element. This component re-renders per `nonce`
 * (scroll / zoom / drag) — that is how every chart-space read here stays fresh — so the
 * DOM read lives at module scope rather than inline in render (react-hooks/refs).
 */
const wrapWidth = (wrapRef: React.RefObject<HTMLDivElement | null>): number | undefined =>
  wrapRef.current?.clientWidth

interface DragState {
  id: string
  moved: boolean
  sx: number
  sy: number
  ox: number
  oy: number
}

export interface WorkbenchNotesProps {
  chart: Chart
  wrapRef: React.RefObject<HTMLDivElement | null>
  notes: WorkbenchNote[]
  setNotes: React.Dispatch<React.SetStateAction<WorkbenchNote[]>>
  selectedId: string | null
  setSelectedId: (id: string | null) => void
  editingId: string | null
  setEditingId: (id: string | null) => void
  nonce: number
}

export function WorkbenchNotes({
  chart,
  wrapRef,
  notes,
  setNotes,
  selectedId,
  setSelectedId,
  editingId,
  setEditingId,
  nonce,
}: WorkbenchNotesProps) {
  void nonce // recompute pixel positions each render (bumped on scroll / zoom)
  const dragRef = useRef<DragState | null>(null)
  const tailRef = useRef<string | null>(null)

  // Convert by timestamp+value only (NOT dataIndex): pagination prepends candles and shifts
  // indexes, but timestamps are stable.
  const toPx = (pt: NotePoint | undefined): { x: number; y: number } | null => {
    if (!pt) return null
    return asPixel(
      chart.convertToPixel(
        { timestamp: pt.timestamp, value: pt.value },
        { paneId: 'candle_pane', absolute: true },
      ),
    )
  }
  const toPt = (x: number, y: number): NotePoint | null => {
    const p = asPoint(chart.convertFromPixel([{ x, y }], { paneId: 'candle_pane', absolute: true }))
    if (!p || p.timestamp == null || p.value == null) return null
    return { timestamp: p.timestamp, dataIndex: p.dataIndex, value: p.value }
  }
  const patch = (id: string, p: Partial<WorkbenchNote>) =>
    setNotes((ns) => ns.map((n) => (n.id === id ? { ...n, ...p } : n)))
  // VIEW mode: a note renders at its true position and may overflow the viewport (scroll to
  // find it) — so it drags freely left/right. EDIT mode ONLY: clamp its left so the whole bubble
  // stays inside the PLOT area (excluding the price/y-axis) and is fully visible while typing.
  const CLAMP_W = 250
  const CLAMP_PAD = 8
  // Plot area width (excludes the price/y-axis). Notes are clipped to it so an overflowing note
  // slides UNDER the axis instead of over it; the edit-mode clamp also keeps clear of the axis.
  const plotW =
    chart.getDom('candle_pane', DomPosition.Main)?.clientWidth ??
    // eslint-disable-next-line react-hooks/refs -- fallback DOM measurement; `nonce` re-renders keep it fresh
    wrapWidth(wrapRef) ??
    99999
  const leftFor = (b: { x: number }, editing: boolean): number => {
    if (!editing) return b.x
    return Math.max(CLAMP_PAD, Math.min(b.x, plotW - CLAMP_W - CLAMP_PAD))
  }
  const remove = (id: string) => {
    setNotes((ns) => ns.filter((n) => n.id !== id))
    setSelectedId(null)
    setEditingId(null)
  }
  const cloneNote = (n: WorkbenchNote) => {
    const shift = (pt: NotePoint): NotePoint => {
      const p = toPx(pt)
      return p ? (toPt(p.x + 18, p.y + 18) ?? pt) : pt
    }
    const id = newNoteId()
    const copy: WorkbenchNote = {
      ...n,
      id,
      pos: shift(n.pos),
      ...(n.kind === 'callout' && n.tail ? { tail: shift(n.tail) } : {}),
    }
    setNotes((ns) => [...ns, copy])
    setSelectedId(id)
    setEditingId(null)
  }

  // Drag the note body to move it (skipped while editing). A press with no drag = a click → edit.
  const onDown = (e: React.PointerEvent<HTMLDivElement>, n: WorkbenchNote) => {
    if (editingId === n.id) return
    e.stopPropagation()
    const b = toPx(n.pos)
    dragRef.current = { id: n.id, moved: false, sx: e.clientX, sy: e.clientY, ox: b?.x ?? 0, oy: b?.y ?? 0 }
    e.currentTarget.setPointerCapture?.(e.pointerId)
    setSelectedId(n.id)
  }
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current
    if (!d) return
    if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < 3) return
    d.moved = true
    const pt = toPt(d.ox + (e.clientX - d.sx), d.oy + (e.clientY - d.sy))
    if (pt) patch(d.id, { pos: pt })
  }
  const onUp = (e: React.PointerEvent<HTMLDivElement>, n: WorkbenchNote) => {
    const d = dragRef.current
    dragRef.current = null
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId)
    } catch {
      /* noop */
    }
    if (d && !d.moved) setEditingId(n.id) // a click (no drag) → edit inline
  }
  // Drag the callout's ⊕ root to re-anchor its tail onto a different candle's O/H/L/C.
  const snapOHLC = (dataIndex: number, value: number, timestamp?: number): NotePoint | null => {
    const bar: KLineData | undefined = chart.getDataList()[dataIndex]
    let v = value
    let best = Infinity
    if (bar) {
      for (const k of ['open', 'high', 'low', 'close'] as const) {
        const d = Math.abs(bar[k] - value)
        if (d < best) {
          best = d
          v = bar[k]
        }
      }
    }
    const ts = bar?.timestamp ?? timestamp
    if (ts == null) return null
    return { timestamp: ts, dataIndex, value: v }
  }
  const onTailDown = (e: React.PointerEvent<HTMLDivElement>, n: WorkbenchNote) => {
    e.stopPropagation()
    tailRef.current = n.id
    setSelectedId(n.id)
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }
  const onTailMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const id = tailRef.current
    if (!id) return
    const rect = wrapRef.current?.getBoundingClientRect()
    const pt = asPoint(
      chart.convertFromPixel(
        [{ x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) }],
        { paneId: 'candle_pane', absolute: true },
      ),
    )
    if (!pt || pt.dataIndex == null || pt.value == null) return
    const snapped = snapOHLC(pt.dataIndex, pt.value, pt.timestamp)
    if (snapped) patch(id, { tail: snapped })
  }
  const onTailUp = (e: React.PointerEvent<HTMLDivElement>) => {
    tailRef.current = null
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId)
    } catch {
      /* noop */
    }
  }

  const commit = (n: WorkbenchNote, text: string) => {
    const t = text.trim()
    if (!t) setNotes((ns) => ns.filter((x) => x.id !== n.id)) // empty → discard
    setEditingId(null)
  }

  return (
    <div className="absolute inset-y-0 left-0 overflow-hidden pointer-events-none" style={{ width: plotW }}>
      {/* Beak — a filled triangle from the bubble's bottom edge down to the O/H/L/C anchor, in
          the bubble's fill so it reads as one continuous speech bubble (TV-style). Its base sits
          just inside the bubble bottom (hidden behind it), so only the taper shows. */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-20" aria-hidden="true">
        {notes
          .filter((n) => n.kind === 'callout')
          .map((n) => {
            const b = toPx(n.pos)
            const t = toPx(n.tail)
            if (!b || !t) return null
            const base = n.bg || CALLOUT_BG
            const edge = darken(base)
            const fill = darken(base, 0.72, 0.5)
            const bl = leftFor(b, editingId === n.id)
            return (
              <g key={n.id}>
                <polygon points={`${bl + 8},${b.y - 1} ${bl + 20},${b.y - 1} ${t.x},${t.y}`} fill={fill} />
                {/* outline the two slanted sides only (base is hidden behind the bubble) */}
                <polyline
                  points={`${bl + 8},${b.y - 1} ${t.x},${t.y} ${bl + 20},${b.y - 1}`}
                  fill="none"
                  stroke={edge}
                  strokeWidth="1"
                />
              </g>
            )
          })}
      </svg>
      {/* Draggable ⊕ root — shown only when the callout is selected; drag to re-anchor onto
          another candle's O/H/L/C. */}
      {notes
        .filter((n) => n.kind === 'callout' && selectedId === n.id)
        .map((n) => {
          const t = toPx(n.tail)
          if (!t) return null
          return (
            <div
              key={`tail-${n.id}`}
              onPointerDown={(e) => onTailDown(e, n)}
              onPointerMove={onTailMove}
              onPointerUp={onTailUp}
              title="Drag to re-anchor"
              className="absolute z-40 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-primary bg-background cursor-move pointer-events-auto"
              style={{ left: t.x, top: t.y }}
            />
          )
        })}

      {notes.map((n) => {
        const b = toPx(n.pos)
        if (!b) return null
        const isCallout = n.kind === 'callout'
        const editing = editingId === n.id
        const selected = selectedId === n.id
        const textStyle: React.CSSProperties = {
          fontSize: `${n.size || 14}px`,
          fontWeight: n.bold ? 700 : 400,
          color: n.color || (isCallout ? CALLOUT_TEXT : 'var(--foreground)'),
          lineHeight: 1.35,
        }
        return (
          <div
            key={n.id}
            className="absolute z-30 pointer-events-auto"
            style={{ left: leftFor(b, editing), top: b.y, transform: 'translateY(-100%)' }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {selected && (
              <div className="absolute bottom-full left-0 mb-1.5">
                <NoteToolbar
                  n={n}
                  onPatch={(p) => patch(n.id, p)}
                  onClone={() => cloneNote(n)}
                  onDelete={() => remove(n.id)}
                />
              </div>
            )}
            <div
              onPointerDown={(e) => onDown(e, n)}
              onPointerMove={onMove}
              onPointerUp={(e) => onUp(e, n)}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'min-w-[44px] max-w-[260px]',
                editing ? 'cursor-text' : 'cursor-move',
                isCallout && 'rounded-md',
                selected && !isCallout && 'outline outline-1 outline-primary/40 rounded-sm',
              )}
              style={
                isCallout
                  ? {
                      background: darken(n.bg || CALLOUT_BG, 0.72, 0.5),
                      border: `1px solid ${darken(n.bg || CALLOUT_BG)}`,
                      padding: '7px 11px',
                      backdropFilter: 'blur(4px)',
                      WebkitBackdropFilter: 'blur(4px)',
                      boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.22), inset 0 -2px 3px rgba(0,0,0,0.18)',
                    }
                  : {
                      background: n.bg || 'transparent',
                      padding: n.bg ? '2px 5px' : 0,
                      borderRadius: n.bg ? 3 : 0,
                    }
              }
            >
              {editing ? (
                <textarea
                  autoFocus
                  rows={1}
                  value={n.text}
                  onFocus={(e) => {
                    e.target.style.height = 'auto'
                    e.target.style.height = `${e.target.scrollHeight}px`
                  }}
                  onChange={(e) => {
                    patch(n.id, { text: e.target.value })
                    e.target.style.height = 'auto'
                    e.target.style.height = `${e.target.scrollHeight}px`
                  }}
                  onBlur={(e) => commit(n, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') e.currentTarget.blur()
                  }}
                  placeholder={isCallout ? 'Type your note…' : 'Text…'}
                  className="block w-[220px] resize-none overflow-hidden bg-transparent outline-none placeholder:opacity-40"
                  style={textStyle}
                />
              ) : (
                <div className="whitespace-pre-wrap break-words" style={textStyle}>
                  {n.text}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Style bar above a selected note: font size, bold, text colour, background colour, clone, delete.
// Buttons preventDefault on mousedown so clicking them never blurs the note's textarea.
function NoteToolbar({
  n,
  onPatch,
  onClone,
  onDelete,
}: {
  n: WorkbenchNote
  onPatch: (p: Partial<WorkbenchNote>) => void
  onClone: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState<'size' | 'color' | 'bg' | null>(null)
  const [drag, setDrag] = useState<DragOffset>({ dx: 0, dy: 0 })
  const noBlur = (e: React.MouseEvent) => e.preventDefault()
  const parse = (c: string, fb: string): { hex: string; op: number } => {
    if (c.startsWith('rgba')) {
      const m = c.match(/[\d.]+/g) ?? []
      return {
        hex: '#' + [m[0], m[1], m[2]].map((x) => Math.round(Number(x)).toString(16).padStart(2, '0')).join(''),
        op: Math.round((Number(m[3]) || 1) * 100),
      }
    }
    return { hex: c || fb, op: 100 }
  }
  const txt = parse(n.color, '#1a1a1a')
  const bg = parse(n.bg, CALLOUT_BG)
  return (
    <div
      onMouseDown={noBlur}
      style={{ transform: `translate(${drag.dx}px, ${drag.dy}px)` }}
      className="flex items-center gap-0.5 rounded-md border border-border bg-popover px-1 py-1 shadow-popover"
    >
      <Grip drag={drag} setDrag={setDrag} />
      <div className="relative">
        <ToolbarButton
          active={open === 'size'}
          title="Font size"
          onClick={() => setOpen(open === 'size' ? null : 'size')}
        >
          <span className="flex items-center gap-1">
            <Type size={14} strokeWidth={1.75} />
            <span className="text-caption tabular-nums">{n.size || 14}</span>
          </span>
        </ToolbarButton>
        {open === 'size' && (
          <BitsPopover>
            {NOTE_SIZES.map((s) => (
              <button
                key={s}
                type="button"
                onMouseDown={noBlur}
                onClick={() => {
                  onPatch({ size: s })
                  setOpen(null)
                }}
                className="w-full flex items-center px-2 py-1.5 rounded-md text-left text-caption text-muted-foreground hover:bg-muted/60 hover:text-foreground cursor-pointer tabular-nums"
              >
                {s}px
              </button>
            ))}
          </BitsPopover>
        )}
      </div>
      <ToolbarButton active={!!n.bold} title="Bold" onClick={() => onPatch({ bold: !n.bold })}>
        <Bold size={14} strokeWidth={2} />
      </ToolbarButton>
      <div className="relative">
        <ToolbarButton
          active={open === 'color'}
          title="Text colour"
          onClick={() => setOpen(open === 'color' ? null : 'color')}
        >
          <span className="flex flex-col items-center gap-0.5">
            <Baseline size={14} strokeWidth={1.75} />
            <span className="block h-[3px] w-4 rounded-sm" style={{ background: txt.hex }} />
          </span>
        </ToolbarButton>
        {open === 'color' && (
          <BitsPopover align="right">
            <ColorPopover
              curColor={txt.hex}
              curOpacity={txt.op}
              onColor={(c) => onPatch({ color: rgba(c, txt.op) })}
              onOpacity={(v) => onPatch({ color: rgba(txt.hex, v) })}
            />
          </BitsPopover>
        )}
      </div>
      <div className="relative">
        <ToolbarButton
          active={open === 'bg'}
          title="Background colour"
          onClick={() => setOpen(open === 'bg' ? null : 'bg')}
        >
          <span className="flex flex-col items-center gap-0.5">
            <PaintBucket size={14} strokeWidth={1.75} />
            <span className="block h-[3px] w-4 rounded-sm" style={{ background: bg.hex }} />
          </span>
        </ToolbarButton>
        {open === 'bg' && (
          <BitsPopover align="right">
            <ColorPopover
              curColor={bg.hex}
              curOpacity={bg.op}
              onColor={(c) => onPatch({ bg: rgba(c, bg.op) })}
              onOpacity={(v) => onPatch({ bg: rgba(bg.hex, v) })}
            />
          </BitsPopover>
        )}
      </div>
      <div className="mx-0.5 h-5 w-px bg-border" />
      <ToolbarButton title="Clone" onClick={onClone}>
        <Copy size={15} strokeWidth={1.75} />
      </ToolbarButton>
      <ToolbarButton title="Delete" onClick={onDelete}>
        <Trash2 size={15} strokeWidth={1.75} />
      </ToolbarButton>
    </div>
  )
}
